import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActividades, useActualizarMuestra } from '@/hooks/useLaboratorio';
import {
  type ActualizarMuestraRequest,
  type CategoriaConcreto,
  type MuestraDetalle,
  PLANTAS_LAB,
} from '@adelante/shared';
import { Save, X } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Modal de edición de una muestra existente.
 *
 * Editables (campos del header que pueden cambiar tras crear):
 *  - Actividad
 *  - Fecha de colado
 *  - F'C objetivo
 *  - Categoría (convencional / autocompactable / otro)
 *  - Tipo de concreto (texto libre)
 *  - Notas
 *
 * NO editables desde acá:
 *  - obra_works_no, id_colada → viene de la colada vinculada (cambiar
 *    eso es operación más sensible, queda para una pantalla aparte).
 *  - numero_muestra → autogenerado, inmutable.
 *
 * El backend ya tiene PATCH /api/lab/muestras/:id y `useActualizarMuestra`.
 */

const FC_COMUNES = [140, 175, 210, 245, 280, 315, 350, 420] as const;

interface Props {
  abierto: boolean;
  cerrar: () => void;
  muestra: MuestraDetalle;
}

export function ModalEditarMuestra({ abierto, cerrar, muestra }: Props) {
  const actividades = useActividades(false);
  const actualizar = useActualizarMuestra();

  const [idActividad, setIdActividad] = useState<number>(muestra.id_actividad);
  const [fechaColado, setFechaColado] = useState(muestra.fecha_colado);
  const [fcObjetivo, setFcObjetivo] = useState(muestra.fc_objetivo);
  const [plantaNombre, setPlantaNombre] = useState(muestra.planta_nombre ?? '');
  const [categoria, setCategoria] = useState<CategoriaConcreto>(
    muestra.categoria_concreto ?? 'convencional',
  );
  const [tipoLibre, setTipoLibre] = useState(muestra.tipo_concreto_libre ?? '');
  const [notas, setNotas] = useState(muestra.notas ?? '');

  // Re-sincronizar al re-abrir con muestra distinta.
  useEffect(() => {
    if (!abierto) return;
    setIdActividad(muestra.id_actividad);
    setFechaColado(muestra.fecha_colado);
    setFcObjetivo(muestra.fc_objetivo);
    setPlantaNombre(muestra.planta_nombre ?? '');
    setCategoria(muestra.categoria_concreto ?? 'convencional');
    setTipoLibre(muestra.tipo_concreto_libre ?? '');
    setNotas(muestra.notas ?? '');
  }, [abierto, muestra]);

  const onGuardar = () => {
    // Solo mandamos campos que cambiaron — el backend acepta PATCH parcial.
    const body: ActualizarMuestraRequest = {};
    if (idActividad !== muestra.id_actividad) body.id_actividad = idActividad;
    if (fechaColado !== muestra.fecha_colado) body.fecha_colado = fechaColado;
    if (fcObjetivo !== muestra.fc_objetivo) body.fc_objetivo = fcObjetivo;
    const plantaNueva = plantaNombre.trim() || null;
    if (plantaNueva !== (muestra.planta_nombre ?? null)) body.planta_nombre = plantaNueva;
    if (categoria !== (muestra.categoria_concreto ?? 'convencional')) {
      body.categoria_concreto = categoria;
    }
    const tipoNuevo = tipoLibre.trim() || null;
    if (tipoNuevo !== muestra.tipo_concreto_libre) body.tipo_concreto_libre = tipoNuevo;
    const notasNuevas = notas.trim() || null;
    if (notasNuevas !== muestra.notas) body.notas = notasNuevas;

    // Si no hay cambios, solo cerramos.
    if (Object.keys(body).length === 0) {
      cerrar();
      return;
    }

    actualizar.mutate(
      { id: muestra.id, ...body },
      {
        onSuccess: (r) => {
          if (r.data) cerrar();
        },
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onOpenChange={(o) => {
        if (!o && !actualizar.isPending) cerrar();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar muestra #{muestra.numero_muestra}</DialogTitle>
          <DialogDescription>
            Los ensayos y cilindros se editan en la tarjeta de cada edad. Acá solo se modifican los
            datos del header de la muestra.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Actividad + Fecha */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="edit-actividad">Actividad</Label>
              <Select value={String(idActividad)} onValueChange={(v) => setIdActividad(Number(v))}>
                <SelectTrigger id="edit-actividad">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(actividades.data?.data?.actividades ?? []).map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edit-fecha">Fecha de colado</Label>
              <Input
                id="edit-fecha"
                type="date"
                value={fechaColado}
                onChange={(e) => setFechaColado(e.target.value)}
              />
            </div>
          </div>

          {/* F'C */}
          <div className="grid gap-1.5">
            <Label>F'C objetivo (kg/cm²)</Label>
            <div className="flex flex-wrap gap-1.5">
              {FC_COMUNES.map((fc) => (
                <Button
                  key={fc}
                  type="button"
                  size="sm"
                  variant={fcObjetivo === fc ? 'default' : 'outline'}
                  onClick={() => setFcObjetivo(fc)}
                  className="h-8 px-3 text-xs"
                >
                  {fc}
                </Button>
              ))}
              <Input
                type="number"
                value={fcObjetivo}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) setFcObjetivo(n);
                }}
                className="h-8 w-24"
                min={1}
                max={2000}
              />
            </div>
          </div>

          {/* Planta */}
          <div className="grid gap-1.5">
            <Label htmlFor="edit-planta">Planta</Label>
            <div className="flex flex-wrap gap-1.5">
              {PLANTAS_LAB.map((p) => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={plantaNombre === p ? 'default' : 'outline'}
                  onClick={() => setPlantaNombre(p)}
                  className="h-8 px-3 text-xs"
                >
                  {p}
                </Button>
              ))}
              <Input
                id="edit-planta"
                value={plantaNombre}
                onChange={(e) => setPlantaNombre(e.target.value)}
                placeholder="Otra…"
                className="h-8 w-32"
                maxLength={50}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Bianca/Roja se derivan de la colada. Las Fiori (u otra planta sin colada Blend) se
              eligen acá.
            </p>
          </div>

          {/* Categoría */}
          <div className="grid gap-1.5">
            <Label>Tipo de concreto</Label>
            <div className="flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant={categoria === 'convencional' ? 'default' : 'outline'}
                onClick={() => setCategoria('convencional')}
                className="h-9 px-4"
              >
                Convencional
              </Button>
              <Button
                type="button"
                size="sm"
                variant={categoria === 'autocompactable' ? 'default' : 'outline'}
                onClick={() => setCategoria('autocompactable')}
                className="h-9 px-4"
              >
                Autocompactable
              </Button>
              <Button
                type="button"
                size="sm"
                variant={categoria === 'otro' ? 'default' : 'outline'}
                onClick={() => setCategoria('otro')}
                className="h-9 px-4"
              >
                Otro
              </Button>
            </div>
          </div>

          {/* Tipo libre */}
          <div className="grid gap-1.5">
            <Label htmlFor="edit-tipo-libre">Descripción del concreto (opcional)</Label>
            <Input
              id="edit-tipo-libre"
              value={tipoLibre}
              onChange={(e) => setTipoLibre(e.target.value)}
              placeholder='Ej: "210 LOSA", "SELLO 280"'
            />
          </div>

          {/* Notas */}
          <div className="grid gap-1.5">
            <Label htmlFor="edit-notas">Notas</Label>
            <textarea
              id="edit-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Observaciones…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={cerrar} disabled={actualizar.isPending}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={onGuardar}
            disabled={actualizar.isPending}
            className="gap-1.5"
          >
            <Save className="h-4 w-4" />
            {actualizar.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
